import { Autocomplete, Button, Chip, Grid, TextField } from '@mui/material';
import { History } from 'history';
import React, { FormEvent } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';

import { Role } from 'app/constants/role';
import { Taxonomy } from 'app/model/taxonomy';
import { User } from 'app/model/user';

import { RootState } from 'app/store';
import { ModelWithoutId } from 'app/type/model-with-id';
import { RouteWithIdParam } from 'app/type/route-with-id-param';

import {
    loadUser,
    prepareCreateUser,
    prepareRoles,
    prepareTaxonomies,
    selectPossibleRoles,
    selectPossibleTaxonomies,
    selectSelectedUser,
    updateUser,
} from './user.slice';
import { selectRoles } from '../auth/auth.slice';

interface UserFormProps extends RouteWithIdParam {
    selected: ModelWithoutId<User>;
    possibleRoles: Record<string, string>;
    possibleTaxonomies: Taxonomy[];
    currentUserRoles: Record<string, boolean>;
    loadUser: (id: string) => void;
    updateUser: (user: User, history: History) => void;
    prepareCreateUser: () => void;
    prepareRoles: () => void;
    prepareTaxonomies: () => void;
}

interface UserFormState {
    user?: User;
    id?: number | string;
}

class UserForm extends React.Component<UserFormProps, UserFormState> {
    public constructor(props: Readonly<UserFormProps> | UserFormProps) {
        super(props);
        this.state = {};

        // binding allows us to use this directly in template without doing '() => this.handleSubmit()'
        this.handleSubmit = this.handleSubmit.bind(this);
        this.handleChange = this.handleChange.bind(this);
    }

    public componentDidMount() {
        this.setupUser();
        this.props.prepareRoles();
        this.props.prepareTaxonomies();
    }

    public handleSubmit(evt: FormEvent) {
        evt.preventDefault();
        if (this.state.user) {
            this.props.updateUser(this.state.user, this.props.history);
        }
    }

    public handleChange(evt: { target: { value: unknown; name: string } }) {
        if (!this.state.user) {
            throw new Error('This should not have happened');
        }

        if (!evt.target.name) {
            throw new Error('Control must have name defined');
        }

        this.setState({
            ...this.state,
            user: {
                ...this.state.user,
                [evt.target.name]: evt.target.value,
            },
        });
    }

    public renderTaxonomyChoices() {
        const txOptions = this.props.possibleTaxonomies;

        return (
            <Autocomplete
                multiple
                id='user-taxonomies'
                value={this.state.user?.taxonomies}
                options={txOptions}
                onChange={(evt, value) => {
                    this.handleChange({ target: { name: 'taxonomies', value } });
                }}
                getOptionLabel={(option) => option.name}
                renderTags={(tagValue, getTagProps) =>
                    tagValue.map((option, index) => <Chip label={option.name} {...getTagProps({ index })} />)
                }
                renderInput={(params) => <TextField {...params} label='User Taxonomies' name='taxonomies' />}
            />
        );
    }

    public renderRoleChoices() {
        let possibleRoles = Object.keys(this.props.possibleRoles).filter((role) => role !== Role.USER);
        if (0 === possibleRoles.length) {
            return '';
        }

        if (!this.props.currentUserRoles.ROLE_ROOT) {
            possibleRoles = possibleRoles.filter((role) => Role.ROOT !== role);
        }

        return (
            <Autocomplete
                multiple
                id='user-roles'
                value={UserForm.sanitizeRoles(this.state.user?.roles || [])}
                onChange={(evt, value) => {
                    // Role.USER should only be set if there are no other roles
                    value = UserForm.sanitizeRoles(value);

                    this.handleChange({ target: { name: 'roles', value } });
                }}
                options={possibleRoles}
                getOptionLabel={(option) => this.props.possibleRoles[option]}
                renderTags={(tagValue, getTagProps) =>
                    tagValue.map((option, index) => (
                        <Chip
                            label={this.props.possibleRoles[option]}
                            {...getTagProps({ index })}
                            /* Role.USER is auto maanged, so do not allow to edit */
                            disabled={option === Role.USER}
                        />
                    ))
                }
                renderInput={(params) => <TextField {...params} label='User Roles' name='roles' />}
            />
        );
    }

    public render() {
        const selectedUser = this.state.user;
        if (!selectedUser) {
            return '';
        }

        return (
            <div>
                <h2>{'new' === this.props.match.params.id ? 'Create' : 'Edit'} User</h2>
                <form onSubmit={this.handleSubmit}>
                    <Grid container spacing={3}>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                name='first'
                                label='First Name'
                                value={selectedUser.first}
                                onChange={this.handleChange}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                name='last'
                                label='Last Name'
                                value={selectedUser.last}
                                onChange={this.handleChange}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                name='username'
                                label='Username'
                                value={selectedUser.username}
                                onChange={this.handleChange}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                name='password'
                                label='Password'
                                type='password'
                                autoComplete='new-password'
                                onChange={this.handleChange}
                            />
                        </Grid>

                        <Grid item xs={6}>
                            {this.renderRoleChoices()}
                        </Grid>
                        <Grid item xs={6}>
                            {this.renderTaxonomyChoices()}
                        </Grid>
                        <Grid item xs={6}>
                            <Button type='submit' variant='contained' color='primary'>
                                Submit
                            </Button>
                        </Grid>
                    </Grid>
                </form>
            </div>
        );
    }

    private setupUser() {
        if (this.props.selected.id) {
            // todo do we make a call just to refresh the account?
            // if user refreshes the page it will refresh the account. So I think not.
            return;
        }

        if ('new' === this.props.match.params.id) {
            // new user url accessed directly
            this.props.prepareCreateUser();

            return;
        }

        this.props.loadUser(this.props.match.params.id);
    }

    public static getDerivedStateFromProps(props: UserFormProps, state: UserFormState) {
        if (undefined === state.user || props.selected.id !== state.id) {
            return {
                id: props.selected.id,
                user: {
                    ...props.selected,
                },
            };
        }

        return state;
    }

    private static sanitizeRoles(roles: readonly string[]): string[] {
        let sanitizedRoles = roles.filter((role) => role !== Role.USER);

        if (0 === sanitizedRoles.length) {
            // we have no other roles so set it to the most basic Role.USEr
            sanitizedRoles = [Role.USER];
        }

        return sanitizedRoles;
    }
}

const mapStateToProps = (state: RootState) => ({
    selected: selectSelectedUser(state),
    possibleRoles: selectPossibleRoles(state),
    possibleTaxonomies: selectPossibleTaxonomies(state),
    currentUserRoles: selectRoles(state),
});

const connected = connect(mapStateToProps, {
    loadUser,
    updateUser,
    prepareCreateUser,
    prepareRoles,
    prepareTaxonomies,
})(withRouter(UserForm));

export { connected as UserForm };
